import { OmsConditionType } from "../oms/conditions";

/**
 * The subset of OmsConditionType values that are product-level and meaningful
 * in a work order context (operating on a single line item).
 */
export const WorkOrderProductConditionTypes = [
  OmsConditionType.SKU,
  OmsConditionType.PRODUCT_NAME,
  OmsConditionType.VARIANT_TITLE,
  OmsConditionType.PRODUCT_TYPE,
  OmsConditionType.PRODUCT_TAG,
  OmsConditionType.ITEM_QUANTITY,
  OmsConditionType.VENDOR,
  OmsConditionType.ITEM_COST,
  OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE,
] as const;

export type WorkOrderConditionType =
  (typeof WorkOrderProductConditionTypes)[number];

/**
 * Which condition types can appear in a WHERE clause for each top-level
 * work order condition type.
 */
export const workOrderConditionToWhereConditionTypes: Record<
  OmsConditionType,
  OmsConditionType[]
> = {
  [OmsConditionType.SKU]: [
    OmsConditionType.PRODUCT_NAME,
    OmsConditionType.VARIANT_TITLE,
    OmsConditionType.PRODUCT_TYPE,
    OmsConditionType.SKU,
    OmsConditionType.PRODUCT_TAG,
    OmsConditionType.ITEM_QUANTITY,
    OmsConditionType.VENDOR,
  ],
  [OmsConditionType.PRODUCT_NAME]: [
    OmsConditionType.PRODUCT_NAME,
    OmsConditionType.VARIANT_TITLE,
    OmsConditionType.PRODUCT_TYPE,
    OmsConditionType.SKU,
    OmsConditionType.PRODUCT_TAG,
    OmsConditionType.ITEM_QUANTITY,
    OmsConditionType.VENDOR,
  ],
  [OmsConditionType.VARIANT_TITLE]: [
    OmsConditionType.PRODUCT_NAME,
    OmsConditionType.VARIANT_TITLE,
    OmsConditionType.PRODUCT_TYPE,
    OmsConditionType.SKU,
    OmsConditionType.PRODUCT_TAG,
    OmsConditionType.ITEM_QUANTITY,
    OmsConditionType.VENDOR,
  ],
  [OmsConditionType.PRODUCT_TYPE]: [
    OmsConditionType.PRODUCT_NAME,
    OmsConditionType.VARIANT_TITLE,
    OmsConditionType.PRODUCT_TYPE,
    OmsConditionType.SKU,
    OmsConditionType.PRODUCT_TAG,
    OmsConditionType.ITEM_QUANTITY,
    OmsConditionType.VENDOR,
  ],
  [OmsConditionType.PRODUCT_TAG]: [
    OmsConditionType.PRODUCT_NAME,
    OmsConditionType.VARIANT_TITLE,
    OmsConditionType.PRODUCT_TYPE,
    OmsConditionType.SKU,
    OmsConditionType.PRODUCT_TAG,
    OmsConditionType.ITEM_QUANTITY,
    OmsConditionType.VENDOR,
  ],
  [OmsConditionType.ITEM_QUANTITY]: [
    OmsConditionType.PRODUCT_NAME,
    OmsConditionType.VARIANT_TITLE,
    OmsConditionType.PRODUCT_TYPE,
    OmsConditionType.SKU,
    OmsConditionType.PRODUCT_TAG,
    OmsConditionType.ITEM_QUANTITY,
    OmsConditionType.VENDOR,
  ],
  [OmsConditionType.VENDOR]: [
    OmsConditionType.PRODUCT_NAME,
    OmsConditionType.VARIANT_TITLE,
    OmsConditionType.PRODUCT_TYPE,
    OmsConditionType.SKU,
    OmsConditionType.PRODUCT_TAG,
    OmsConditionType.ITEM_QUANTITY,
    OmsConditionType.VENDOR,
  ],
  [OmsConditionType.ITEM_COST]: [],
  [OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE]: [
    OmsConditionType.PRODUCT_NAME,
    OmsConditionType.VARIANT_TITLE,
    OmsConditionType.PRODUCT_TYPE,
    OmsConditionType.SKU,
    OmsConditionType.PRODUCT_TAG,
    OmsConditionType.ITEM_QUANTITY,
    OmsConditionType.VENDOR,
  ],
  // Non-product conditions — not used in work orders but needed for the Record
  [OmsConditionType.ORDER_TAG]: [],
  [OmsConditionType.SELECTED_SHIPPING]: [],
  [OmsConditionType.PACKAGE_WEIGHT_OZ]: [],
  [OmsConditionType.TOTAL_VALUE]: [],
  [OmsConditionType.TAXES_PAID]: [],
  [OmsConditionType.DESTINATION_COUNTRY]: [],
  [OmsConditionType.DESTINATION_PROVINCE]: [],
  [OmsConditionType.DELIVERY_METHOD]: [],
  [OmsConditionType.STREET_ADDRESS]: [],
  [OmsConditionType.STREET_ADDRESS_LIST_INPUT]: [],
  [OmsConditionType.SALES_CHANNEL]: [],
  [OmsConditionType.FULFILLMENT_LOCATION]: [],
  [OmsConditionType.ORIGIN_STORE]: [],
  [OmsConditionType.ORDER_NOTES]: [],
  [OmsConditionType.FRAUD_RISK]: [],
  [OmsConditionType.AVAILABLE_INVENTORY]: [],
  [OmsConditionType.MARKETPLACE]: [],
  [OmsConditionType.SHIPMENT_TAG]: [],
  [OmsConditionType.CUSTOM_STRING_1]: [],
  [OmsConditionType.CUSTOM_STRING_2]: [],
  [OmsConditionType.CUSTOM_STRING_3]: [],
  [OmsConditionType.CUSTOM_NUMBER_1]: [],
  [OmsConditionType.CUSTOM_NUMBER_2]: [],
  [OmsConditionType.CUSTOM_NUMBER_3]: [],
  [OmsConditionType.CUSTOM_DATE_1]: [],
  [OmsConditionType.CUSTOM_DATE_2]: [],
  [OmsConditionType.CUSTOM_DATE_3]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3]: [],
  [OmsConditionType.CUSTOMER_NAME]: [],
  [OmsConditionType.CUSTOMER_ORDER_COUNT]: [],
  [OmsConditionType.CUSTOMER_TAG]: [],
  [OmsConditionType.USPS_ZONE]: [],
  [OmsConditionType.ADDRESS_VERIFIED]: [],
};
